import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { CheckCircle2, ChevronRight, ChevronLeft, FileText, Users, Layers, ListChecks, Settings, Mail, Eye, Rocket, Search, AlertTriangle } from 'lucide-react';
import { getRole } from '../data/framework';
import { PEOPLE } from '../data/demoOrganisation';
import { domainProgress, questionnaireSize, standardSelection, substitutedCount } from '../utils/framework';
import { RoleStep } from '../components/assessment/RoleStep';
import { DomainsStep } from '../components/assessment/DomainsStep';
import { QuestionPicker } from '../components/assessment/QuestionPicker';
import { QuestionnairePreview } from '../components/assessment/QuestionnairePreview';
const STEPS = [{
  id: 1,
  title: 'Assessment Details',
  icon: FileText
}, {
  id: 2,
  title: 'Type & Role',
  icon: Users
}, {
  id: 3,
  title: 'Competency Domains',
  icon: Layers
}, {
  id: 4,
  title: 'Review Questions',
  icon: ListChecks
}, {
  id: 5,
  title: 'Rating Scale',
  icon: Settings
}, {
  id: 6,
  title: 'Invite Respondents',
  icon: Mail
}, {
  id: 7,
  title: 'Review',
  icon: Eye
}, {
  id: 8,
  title: 'Launch',
  icon: Rocket
}];
const SUGGESTED = PEOPLE.slice(0, 5).map(p => p.name);
const LAST_STEP = STEPS.length;
const REVIEW_STEP = 7;
export function CreateAssessment() {
  const initial = useScreenInit();
  const initialRoleId = typeof initial.role === 'string' && getRole(initial.role) ? initial.role : null;
  const initialRole = initialRoleId ? getRole(initialRoleId) : undefined;
  const [currentStep, setCurrentStep] = useState<number>(typeof initial.step === 'number' ? initial.step : 1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dueDate: '',
    type: '360',
    scale: '5'
  });
  const [roleId, setRoleId] = useState<string | null>(initialRoleId);
  const [selected, setSelected] = useState<number[]>(initialRole ? standardSelection(initialRole) : []);
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);
  const role = roleId ? getRole(roleId) : undefined;
  const total = role ? questionnaireSize(role) : 0;
  const incompleteDomains = role ? role.domains.filter(d => !domainProgress(d, selected).complete) : [];
  const selectionComplete = !!role && incompleteDomains.length === 0;
  const swappedCount = role ? substitutedCount(role, selected) : 0;
  const canContinue = (currentStep !== 2 || !!role) && (currentStep !== 3 || !!role) && (currentStep !== 4 || selectionComplete) && (currentStep !== REVIEW_STEP || selectionComplete);
  const applyRole = (id: string) => {
    const next = getRole(id);
    if (!next) return;
    setRoleId(id);
    setSelected(standardSelection(next));
  };
  const handleRoleSelect = (id: string) => {
    if (id === roleId) return;
    if (swappedCount > 0) {
      setPendingRoleId(id);
      return;
    }
    applyRole(id);
  };
  const confirmRoleChange = () => {
    if (!pendingRoleId) return;
    applyRole(pendingRoleId);
    setPendingRoleId(null);
  };
  const swapItem = (fromNo: number, toNo: number) => {
    setSelected(prev => prev.includes(toNo) ? prev : prev.map(n => n === fromNo ? toNo : n));
  };
  const resetToStandard = () => {
    if (role) setSelected(standardSelection(role));
  };
  const handleNext = () => {
    if (canContinue && currentStep < LAST_STEP) setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const renderNoRole = () => <div className="border border-dashed border-slate-300 rounded-xl py-16 text-center" data-id="element-960" data-mp="3cmz38cjdqa4y">
      <Layers className="h-6 w-6 text-slate-400 mx-auto mb-3" data-id="element-961" data-mp="3hmljyvyf28ii" />
      <p className="text-sm font-medium text-slate-900" data-id="element-962" data-mp="3hjz2o4lq4en3">No role selected</p>
      <p className="text-sm text-slate-500 mt-1 mb-4" data-id="element-963" data-mp="rwqaqv026m41">
        Choose the role being assessed to load its competency domains.
      </p>
      <button type="button" onClick={() => setCurrentStep(2)} className="text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-964" data-mp="uhfsq8b7cgtc">
        Choose a role
      </button>
    </div>;
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-965" data-mp="zexy5oq0gdwl">
            <div data-id="element-966" data-mp="1dfiiipbkqhs7">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-967" data-mp="1dez5nn0s6jl1">
                Assessment Details
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-968" data-mp="19cm47at2h7u2">
                Provide the basic information for this assessment cycle.
              </p>
            </div>
            <div className="space-y-4" data-id="element-969" data-mp="1i81vpl6cnawh">
              <div data-id="element-970" data-mp="1i9cv78srhwhs">
                <label htmlFor="assessment-name" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-971" data-mp="1x419ncg5459j">
                  Assessment Name *
                </label>
                <input id="assessment-name" type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="e.g., 2026 Board Effectiveness Review" className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-972" data-mp="2bqq8rd6hcgfe" />
              </div>
              <div data-id="element-973" data-mp="1wvtrybklukqp">
                <label htmlFor="assessment-description" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-974" data-mp="1j2avav2zy666">
                  Description
                </label>
                <textarea id="assessment-description" rows={3} value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} placeholder="Brief description of the assessment goals..." className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-975" data-mp="193mqiy00vnkf" />
              </div>
              <div data-id="element-976" data-mp="g7vou1vocjvl">
                <label htmlFor="assessment-due" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-977" data-mp="1dy0c5wan91rb">
                  Due Date *
                </label>
                <input id="assessment-due" type="date" value={formData.dueDate} onChange={e => setFormData({
                ...formData,
                dueDate: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-978" data-mp="13yhslgynbhzs" />
              </div>
            </div>
          </div>;
      case 2:
        return <RoleStep type={formData.type} roleId={roleId} onTypeChange={type => setFormData({
          ...formData,
          type
        })} onRoleSelect={handleRoleSelect} data-id="element-979" data-mp="245em6o4ig5ay" />;
      case 3:
        return role ? <DomainsStep role={role} data-id="element-980" data-mp="2ix5ae0ijsgz6" /> : renderNoRole();
      case 4:
        return role ? <QuestionPicker role={role} selected={selected} onSwap={swapItem} onResetAll={resetToStandard} data-id="element-981" data-mp="2mxfoy1keofpn" /> : renderNoRole();
      case 5:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-982" data-mp="2wvddkq66geis">
            <div data-id="element-983" data-mp="xk2h42d349js">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-984" data-mp="xklquvskzrmq">
                Rating Scale
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-985" data-mp="12k824zoumxsw">
                Define how respondents will rate each statement.
              </p>
            </div>
            <div className="space-y-4" data-id="element-986" data-mp="2zfjhixusk4zc">
              <div data-id="element-987" data-mp="2zei6gtdrzihm">
                <label htmlFor="assessment-scale" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-988" data-mp="3e5h9lovudett">
                  Scale Type
                </label>
                <select id="assessment-scale" value={formData.scale} onChange={e => setFormData({
                ...formData,
                scale: e.target.value
              })} className="w-full md:w-1/2 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm bg-white" data-id="element-989" data-mp="2lga1by6l5avd">
                  <option value="5" data-id="element-990" data-mp="an7a2hpleu8e">5-Point Scale</option>
                  <option value="7" data-id="element-991" data-mp="5q7406sqtrha">7-Point Scale</option>
                  <option value="10" data-id="element-992" data-mp="5rrvfnjrguax">10-Point Scale</option>
                </select>
              </div>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-993" data-mp="dbjaf40tkjkk">
                <h4 className="text-sm font-medium text-slate-900 mb-3" data-id="element-994" data-mp="i9ktvq25qkwk">
                  Preview ({formData.scale}-Point)
                </h4>
                <div className="flex justify-between items-start text-sm gap-1" data-id="element-995" data-mp="89k53olirckz">
                  {Array.from({
                  length: Number(formData.scale)
                }, (_, i) => {
                  const n = i + 1;
                  const last = Number(formData.scale);
                  return <div key={n} className="text-center flex-1" data-id="element-996" data-mp="n7cmynejgd2c">
                        <div className="w-8 h-8 rounded-full bg-white border border-slate-300 flex items-center justify-center mb-1 font-medium mx-auto" data-id="element-997" data-mp="n7484pyuuxoi">
                          {n}
                        </div>
                        {(n === 1 || n === last) && <span className="text-xs text-slate-500" data-id="element-998" data-mp="1jqaefgl8okit">
                            {n === 1 ? 'Strongly Disagree' : 'Strongly Agree'}
                          </span>}
                      </div>;
                })}
                </div>
              </div>
            </div>
          </div>;
      case 6:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-999" data-mp="2mtdp0z2yoi54">
            <div data-id="element-1000" data-mp="292s65adic878">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-1001" data-mp="2928lhlwh35pi">
                Invite Respondents
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1002" data-mp="2dujurfd3s3uq">
                Select the subjects who will be evaluated in this cycle.
              </p>
            </div>
            <div className="relative" data-id="element-1003" data-mp="243fhck32sr20">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1004" data-mp="244gwp0r4jzpj" />
              <input type="text" aria-label="Search users or departments" placeholder="Search users or departments..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-1005" data-mp="1q4z224r5ds89" />
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-1006" data-mp="1kif1doo0x54f">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1007" data-mp="1fisnre4a9mv3">
                Suggested Users
              </div>
              <div className="divide-y divide-slate-100" data-id="element-1008" data-mp="1ph017mzd5msc">
                {SUGGESTED.map(name => <div key={name} className="flex items-center justify-between p-3 hover:bg-slate-50" data-id="element-1009" data-mp="238dh8qy2c2im">
                    <div className="flex items-center" data-id="element-1010" data-mp="1z7bew18unacf">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3" data-id="element-1011" data-mp="2d61f3pqytq50">
                        {name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="text-sm font-medium text-slate-900" data-id="element-1012" data-mp="19wgirgjylns8">
                        {name}
                      </span>
                    </div>
                    <button className="text-sm text-slate-600 hover:text-slate-900 font-medium px-3 py-1 border border-slate-200 rounded hover:bg-white" data-id="element-1013" data-mp="1jl6u3j0w2o3w">
                      Add
                    </button>
                  </div>)}
              </div>
            </div>
          </div>;
      case 7:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1014" data-mp="1xmj7pnskudmm">
            <div data-id="element-1015" data-mp="22kuf7rvegc1o">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-1016" data-mp="22j9ka156ar3a">
                Review Assessment
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1017" data-mp="391hft0sf2nw">
                Please review the details and questionnaire before launching.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6" data-id="element-1018" data-mp="nu1nenc2lok">
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1019" data-mp="3s0y4yem40y46">
                {[['Name', formData.name || 'Untitled Assessment'], ['Due Date', formData.dueDate || 'Not set'], ['Type', formData.type === 'self' ? 'Self-Assessment' : `${formData.type}° Feedback`], ['Role', role?.name ?? 'Not selected'], ['Scale', `${formData.scale}-Point`], ['Questions', role ? swappedCount > 0 ? `${swappedCount} of ${total} customised` : `Standard set · ${total}` : '—']].map(([label, value]) => <div key={label} data-id="element-1020" data-mp="2qc45euxu94qe">
                    <dt className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1021" data-mp="2qbun7tj1c1v9">
                      {label}
                    </dt>
                    <dd className="font-medium text-slate-900" data-id="element-1022" data-mp="i1bikzswlwcy">{value}</dd>
                  </div>)}
              </dl>
            </div>

            {role && incompleteDomains.length > 0 && <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-id="element-1023" data-mp="83dvifms65lx">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" data-id="element-1024" data-mp="10tw41w69wz6q" />
                <div className="text-sm text-amber-800" data-id="element-1025" data-mp="10s2yylbb6n0u">
                  {incompleteDomains.length}{' '}
                  {incompleteDomains.length === 1 ? 'domain is' : 'domains are'}{' '}
                  incomplete: {incompleteDomains.map(d => d.name).join(', ')}.{' '}
                  <button type="button" onClick={() => setCurrentStep(4)} className="font-medium underline underline-offset-2" data-id="element-1026" data-mp="vsztizrf2esc">
                    Finish selecting questions
                  </button>
                </div>
              </div>}

            {role ? <div data-id="element-1027" data-mp="3437km8d8qcpv">
                <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-1028" data-mp="341ms0oahl3un">
                  Questionnaire preview
                </h3>
                <QuestionnairePreview role={role} selected={selected} data-id="element-1029" data-mp="3ixciozqma55p" />
              </div> : renderNoRole()}
          </div>;
      case 8:
        return <div className="py-12 text-center animate-in fade-in zoom-in duration-500" data-id="element-1030" data-mp="2lbyca2f7htzv">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6" data-id="element-1031" data-mp="2l9u1d0iw3uq3">
              <Rocket className="h-8 w-8 text-emerald-600" data-id="element-1032" data-mp="2va4ekl4xd259" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1033" data-mp="2v81e1765bqma">
              Assessment Launched!
            </h2>
            <p className="text-slate-500 max-w-md mx-auto mb-8" data-id="element-1034" data-mp="22lgkq16414g3">
              Your {role ? `${role.name} ` : ''}assessment with {selected.length}{' '}
              questions has been created and invitations have been sent to the
              selected respondents.
            </p>
            <div className="flex justify-center space-x-4" data-id="element-1035" data-mp="2w93as6ij885w">
              <Link to="/app/assessments" className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1036" data-mp="3137fsk88m64c">
                View Assessments
              </Link>
              <Link to="/app" className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-1037" data-mp="2dbsb4kszncpx">
                Go to Dashboard
              </Link>
            </div>
          </div>;
      default:
        return null;
    }
  };
  const pendingRole = pendingRoleId ? getRole(pendingRoleId) : undefined;
  return <div className="max-w-6xl mx-auto animate-in fade-in duration-500" data-id="element-1038" data-mp="2jhby56dwboh">
      <div className="mb-8" data-id="element-1039" data-mp="1n21mnpskogxz">
        <Link to="/app/assessments" className="text-sm font-medium text-slate-500 hover:text-slate-900 flex items-center mb-4" data-id="element-1040" data-mp="1n19oq8bi674j">
          <ChevronLeft className="h-4 w-4 mr-1" data-id="element-1041" data-mp="1dci28fp7of10" /> Back to Assessments
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900" data-id="element-1042" data-mp="1daf1p1qfn3i1">
          Create New Assessment
        </h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row min-h-[600px]" data-id="element-1043" data-mp="14bto7kztieo4">
        {/* Sidebar Progress */}
        <div className="w-full md:w-60 bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 p-6 shrink-0 hidden md:block" data-id="element-1044" data-mp="zbfelyikiwnz">
          <div className="space-y-2" data-id="element-1045" data-mp="pestd8mby3lf">
            {STEPS.map(step => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isPast = currentStep > step.id;
            return <div key={step.id} className={`flex items-center p-2 rounded-lg transition-colors ${isActive ? 'bg-white shadow-sm border border-slate-200' : 'border border-transparent'}`} data-id="element-1046" data-mp="2jtveyv89d1j2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 shrink-0 ${isActive ? 'bg-slate-900 text-white' : isPast ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-400'}`} data-id="element-1047" data-mp="1juw9srfpad82">
                    {isPast ? <CheckCircle2 className="h-5 w-5" data-id="element-1048" data-mp="1evtevztfpndt" /> : <Icon className="h-4 w-4" data-id="element-1049" data-mp="1etqfwqab0jwy" />}
                  </div>
                  <span className={`text-sm font-medium ${isActive ? 'text-slate-900' : isPast ? 'text-slate-700' : 'text-slate-400'}`} data-id="element-1050" data-mp="10y4tmqvgge88">
                    {step.title}
                  </span>
                </div>;
          })}
          </div>
          {role && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-1051" data-mp="nob2f1hujyah">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1052" data-mp="nokt6rapu9h8">
                Role
              </div>
              <div className="text-sm font-medium text-slate-900" data-id="element-1053" data-mp="jkwl87i69bvg">
                {role.name}
              </div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-1054" data-mp="sno06ha9apru">
                {selected.length} of {total} questions
              </div>
            </div>}
        </div>

        {/* Mobile Progress Bar */}
        <div className="md:hidden p-4 border-b border-slate-200 bg-slate-50" data-id="element-1055" data-mp="12dqr8bxthtnj">
          <div className="flex items-center justify-between text-sm font-medium text-slate-700 mb-2" data-id="element-1056" data-mp="1gd03j0iwk3m8">
            <span data-id="element-1057" data-mp="1be5cxcep1ark">
              Step {currentStep} of {STEPS.length}
            </span>
            <span data-id="element-1058" data-mp="3i4d97rsp2fjc">{Math.round(currentStep / STEPS.length * 100)}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-1059" data-mp="3i66ileuozdv1">
            <div className="bg-slate-900 h-2 rounded-full transition-all duration-300" style={{
            width: `${currentStep / STEPS.length * 100}%`
          }} data-id="element-1060" data-mp="3d6k1uvfnnfhh" />
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0" data-id="element-1061" data-mp="2u8vauhn17m07">
          <div className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-1062" data-mp="2u8lfsfn4qonn">
            {renderStepContent()}
          </div>

          {currentStep < LAST_STEP && <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center gap-4 mt-auto" data-id="element-1063" data-mp="22hffnz7esqyd">
              <button onClick={handleBack} disabled={currentStep === 1} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1064" data-mp="1xjw08vyz684u">
                Back
              </button>
              <div className="flex items-center gap-3" data-id="element-1065" data-mp="3hypm4urpd2ic">
                {currentStep === 4 && role && !selectionComplete && <span className="hidden sm:inline text-xs text-slate-500" data-id="element-1066" data-mp="3qfghu6gz3yx9">
                    {incompleteDomains.length} domain
                    {incompleteDomains.length === 1 ? '' : 's'} left
                  </span>}
                <button onClick={handleNext} disabled={!canContinue} className="px-6 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-1067" data-mp="3gjd566ohwhw2">
                  {currentStep === REVIEW_STEP ? 'Launch Assessment' : 'Continue'}
                  {currentStep !== REVIEW_STEP && <ChevronRight className="h-4 w-4 ml-1" data-id="element-1068" data-mp="89blp7tlipth" />}
                </button>
              </div>
            </div>}
        </div>
      </div>

      {/* Role change confirmation */}
      {pendingRole && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1069" data-mp="10rpbck4s35hh">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setPendingRoleId(null)} data-id="element-1070" data-mp="1al1suuouwa43" />
          <div role="alertdialog" aria-modal="true" aria-labelledby="role-change-title" className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6" data-id="element-1071" data-mp="15lgxeu8wajw4">
            <div className="flex items-start gap-3" data-id="element-1072" data-mp="rn8u84l9mmci">
              <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0" data-id="element-1073" data-mp="rotn7prxlx89">
                <AlertTriangle className="h-5 w-5 text-amber-600" data-id="element-1074" data-mp="mupep230ph52" />
              </div>
              <div data-id="element-1075" data-mp="hy8ugiyxvmx9">
                <h2 id="role-change-title" className="text-base font-semibold text-slate-900" data-id="element-1076" data-mp="hvct0knvug42">
                  Change role to {pendingRole.name}?
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-1077" data-mp="3y6dcyyggxj2">
                  Each role has different competency domains, so your{' '}
                  {swappedCount} swapped{' '}
                  {swappedCount === 1 ? 'question' : 'questions'} will be
                  replaced with the new role's standard set.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-1078" data-mp="2ojv7xn1vw92o">
              <button onClick={() => setPendingRoleId(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1079" data-mp="2tjzthrhnftzr">
                Keep current role
              </button>
              <button onClick={confirmRoleChange} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800" data-id="element-1080" data-mp="25uygdqxvfuo5">
                Change role
              </button>
            </div>
          </div>
        </div>}
    </div>;
}